<template>
    <div class="container-fluid py-4">

        <div class="row mb-4">
            <div class="col-12">
                <h2 class="mb-4">Employees</h2>

                <BButton variant="primary" class="mb-3" @click="openModal">
                    <i class="fas fa-plus-circle me-2"></i>Add New Employee
                </BButton>

                <BCard>
                    <BCardHeader>
                        <h3 class="mb-0">Employees</h3>
                    </BCardHeader>
                    <BCardBody>
                        <!-- Desktop Table -->
                        <div class="table-responsive d-none d-md-block">
                            <BTable striped hover :items="employeeTableItems" :fields="tableFields" :busy="loading">
                                <template #table-busy>
                                    <div class="text-center text-primary my-2">
                                        <BSpinner class="align-middle me-2"></BSpinner>
                                        <strong>Loading...</strong>
                                    </div>
                                </template>

                                <template #cell(confirmed)="data">
                                    <BBadge :variant="data.value ? 'success' : 'warning'">
                                        {{ data.value ? 'Yes' : 'No' }}
                                    </BBadge>
                                </template>

                                <template #cell(status)="data">
                                    <BBadge :variant="data.value ? 'danger' : 'success'">
                                        {{ data.value ? 'Blocked' : 'Active' }}
                                    </BBadge>
                                </template>

                                <template #cell(actions)="data">
                                    <div class="btn-group btn-group-sm">
                                        <BButton variant="outline-primary" @click="editUser(data.item.rawData)">
                                            <i class="fas fa-edit"></i>
                                        </BButton>
                                        <BButton
                                            :variant="data.item.rawData.blocked ? 'outline-success' : 'outline-danger'"
                                            @click="toggleBlockUser(data.item.rawData)">
                                            <i :class="data.item.rawData.blocked ? 'fas fa-unlock' : 'fas fa-lock'"></i>
                                        </BButton>
                                    </div>
                                </template>
                            </BTable>
                        </div>

                        <!-- Mobile Cards -->
                        <div v-if="loading" class="d-md-none text-center py-4">
                            <BSpinner variant="primary"></BSpinner>
                            <p class="mt-2">Loading users...</p>
                        </div>

                        <div v-else class="row row-cols-1 g-3 d-md-none">
                            <div v-for="user in employeeUsers" :key="user.documentId" class="col">
                                <BCard :border-variant="user.blocked ? 'danger' : null">
                                    <BCardBody>
                                        <BCardTitle>{{ user.fullname }}</BCardTitle>
                                        <BCardSubTitle class="mb-2 text-muted">{{ user.email }}</BCardSubTitle>

                                        <BListGroup flush class="mb-3">
                                            <BListGroupItem class="d-flex justify-content-between">
                                                <span>Role:</span>
                                                <span>{{ getRoleName(user.role?.id) }}</span>
                                            </BListGroupItem>
                                            <BListGroupItem class="d-flex justify-content-between">
                                                <span>Confirmed:</span>
                                                <BBadge :variant="user.confirmed ? 'success' : 'warning'">
                                                    {{ user.confirmed ? 'Yes' : 'No' }}
                                                </BBadge>
                                            </BListGroupItem>
                                            <BListGroupItem class="d-flex justify-content-between">
                                                <span>Status:</span>
                                                <BBadge :variant="user.blocked ? 'danger' : 'success'">
                                                    {{ user.blocked ? 'Blocked' : 'Active' }}
                                                </BBadge>
                                            </BListGroupItem>
                                        </BListGroup>

                                        <div class="d-grid gap-2 d-flex justify-content-end">
                                            <BButton variant="outline-primary" size="sm" @click="editUser(user)">
                                                <i class="fas fa-edit"></i> Edit
                                            </BButton>
                                            <BButton :variant="user.blocked ? 'outline-success' : 'outline-danger'"
                                                size="sm" @click="toggleBlockUser(user)">
                                                <i :class="user.blocked ? 'fas fa-unlock' : 'fas fa-lock'"></i>
                                                {{ user.blocked ? 'Unblock' : 'Block' }}
                                            </BButton>
                                        </div>
                                    </BCardBody>
                                </BCard>
                            </div>
                        </div>
                    </BCardBody>
                </BCard>
            </div>
        </div>

        <!-- User Form Modal -->
        <BModal v-model="showModal" :title="editingUser ? 'Edit Employee' : 'Add New Employee'" size="lg" @hidden="resetForm"
            :busy="formLoading">
            <BForm @submit.prevent="submitUser">
                <BRow class="g-3">
                    <BCol md="6">
                        <BFormGroup label="Fullname" label-for="fullname">
                            <BFormInput id="fullname" v-model="userForm.fullname" required :state="fullnameState" />
                            <BFormInvalidFeedback v-if="!fullnameState">
                                Fullname is required
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>

                    <BCol md="6">
                        <BFormGroup label="Email" label-for="email">
                            <BFormInput type="email" id="email" v-model="userForm.email" required :state="emailState" />
                            <BFormInvalidFeedback v-if="!emailState">
                                Please enter email 
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>

   <BCol md="6">
    <BFormGroup label="Password" label-for="password">

        <div class="password-wrapper">
<BFormInput
  :type="showPassword ? 'text' : 'password'"
  id="password"
  v-model="userForm.password"
  :required="editingUser ? false : true"
  :state="passwordState"
  autocomplete="new-password"
  :placeholder="editingUser ? '' : 'Enter password'"
/>

            <button
                type="button"
                class="password-toggle"
                @click="showPassword = !showPassword"
            >
<i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
            </button>

        </div>

        <BFormText v-if="editingUser">
           Leave blank to keep current password 
        </BFormText>

        <!-- Password Strength -->
        <div
            v-if="userForm.password"
            class="password-strength mt-2"
        >
            <div class="strength-bar">
                <div
                    class="strength-fill"
                    :class="strengthClass"
                    :style="{ width: strengthWidth }"
                ></div>
            </div>

            <small :class="strengthClass">
                {{ passwordStrength }}
            </small>
        </div>

        <!-- Requirements -->
        <ul
            v-if="userForm.password"
            class="requirements"
        >
            <li :class="{ valid: validations?.length }">
                8+ characters
            </li>

            <li :class="{ valid: validations?.uppercase }">
                Uppercase letter
            </li>

            <li :class="{ valid: validations?.lowercase }">
                Lowercase letter
            </li>

            <li :class="{ valid: validations?.number }">
                Number
            </li>

            <li :class="{ valid: validations?.special }">
                Special character
            </li>
        </ul>

        <BFormInvalidFeedback v-if="!passwordState">
            Password must be strong enough
        </BFormInvalidFeedback>

    </BFormGroup>
</BCol>

                    <BCol md="6">
                        <BFormGroup label="Role" label-for="role">
                            <BFormSelect id="role" v-model="userForm.role" required :state="roleState">
                                <BFormSelectOption :value="null" disabled>Select a role</BFormSelectOption>
                                <BFormSelectOption v-for="role in roles" :key="role.id" :value="role.id">
                                    {{ role.name }}
                                </BFormSelectOption>
                            </BFormSelect>
                            <BFormInvalidFeedback v-if="!roleState">
                                Please select a role
                            </BFormInvalidFeedback>
                        </BFormGroup>
                    </BCol>

                    <BCol md="6">
                        <BFormGroup label="Rate per hour" label-for="rateperhour">
                            <BFormInput id="rateperhour" v-model="userForm.rateperhour"  type="number" step="0.01" />
                        </BFormGroup>
                    </BCol>

                    <BCol md="6">
                        <BFormGroup label="Rate per day" label-for="rateperday">
                            <BFormInput id="rateperday" v-model="userForm.rateperday"  type="number" step="0.01" />
                        </BFormGroup>
                    </BCol>


                    <BCol md="6">
                        <BFormGroup>
                            <BFormCheckbox id="confirmed" v-model="userForm.confirmed" switch>
                                Confirmed
                            </BFormCheckbox>
                        </BFormGroup>
                    </BCol>

                    <BCol md="6" v-if="editingUser">
                        <BFormGroup>
                            <BFormCheckbox id="blocked" v-model="userForm.blocked" switch>
                                Blocked
                            </BFormCheckbox>
                        </BFormGroup>
                    </BCol>

 <BCol md="6">
    <BFormGroup label="Assign Office Location">
        <div v-for="ch in carehomes" :key="ch.documentId" class="mb-2">
            <div class="form-check">
                <input class="form-check-input" type="checkbox"
                       :id="`ch-${ch.documentId}`"
                       :value="ch.documentId"
                       v-model="userForm.carehomes" />
                <label class="form-check-label" :for="`ch-${ch.documentId}`">
                    {{ ch.streetname }}, {{ ch.housenumber }}
                </label>
            </div>
        </div>
    </BFormGroup>
</BCol>
                </BRow>
            </BForm>

            <template #footer>
                <BButton variant="secondary" :disabled="formLoading" @click="closeModal">
                    Close
                </BButton>
                <BButton variant="primary" @click="submitUser" :disabled="formLoading || !formValid">
                    <span v-if="formLoading" class="spinner-border spinner-border-sm" role="status"></span>
                    {{ editingUser ? 'Update Employee' : 'Add Employee' }}
                </BButton>
            </template>
        </BModal>

        <BToast :modelValue="!!toastMessage" @update:modelValue="toastMessage = ''">
            {{ toastMessage }}
        </BToast>
    </div>
</template>

<script>
import { ref, onMounted, computed, watchEffect, watch } from 'vue';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { getBread } from "../components/values";
import { BASE_URL } from '@/config';
//import { UPDATE_CAREHOME, GET_ROLES, GET_CAREHOMES, GET_USERS_BY_ADMIN, UPDATE_USER } from '@/hooks/queries';
import { CREATE_USER, UPDATE_CAREHOME, GET_CAREHOMES_BY_USER, GET_USERS_BY_ADMIN, UPDATE_USER} from '@/hooks/queries';
import { useQuery, useMutation } from '@vue/apollo-composable';


export default {
    name: 'UserManagement',

    setup() {
        // API configuration
        const API_URL = BASE_URL;
        const token = localStorage.getItem('jwt');
       // console.log(token);

        const userString = localStorage.getItem('user');
        const user = userString ? JSON.parse(userString) : null;
        const adminname = user?.fullname || user?.username || user?.email || '';
        const adminid = user?.id || '';
        const documentId = user?.documentId || '';

        // Reactive data
        const users = ref([]);
        const roles = ref([]);
        const userForm = ref({
            id: null,
            email: '',
            fullname: '',
            rateperhour: 0.0,
            rateperday: 0.0,
            password: '',
            confirmed: true,
            role: null,
            blocked: false,
            carehomes: []
        });
        const editingUser = ref(false);
        const showModal = ref(false);
        const loading = ref(false);
        const formLoading = ref(false);
        const toastMessage = ref('');
        const toast = useToast();

const showPassword = ref(false);

const validations = computed(() => ({
    length: userForm.value.password?.length >= 8,
    uppercase: /[A-Z]/.test(userForm.value.password || ""),
    lowercase: /[a-z]/.test(userForm.value.password || ""),
    number: /\d/.test(userForm.value.password || ""),
    special: /[!@#$%^&*(),.?":{}|<>-]/.test(userForm.value.password || ""),
}));

const passwordScore = computed(() => {
    return Object.values(validations.value).filter(Boolean).length;
});

const passwordStrength = computed(() => {
    const score = passwordScore.value;

    if (score <= 2) return "Weak Password";
    if (score <= 4) return "Medium Password";

    return "Strong Password";
});

const strengthClass = computed(() => {
    const score = passwordScore.value;

    if (score <= 2) return "weak";
    if (score <= 4) return "medium";

    return "strong";
});

const strengthWidth = computed(() => {
    return `${(passwordScore.value / 5) * 100}%`;
});


        const { mutate: updateCarehome } = useMutation(UPDATE_CAREHOME);
        const { mutate: updateUser } = useMutation(UPDATE_USER);
        const {mutate: createUser} = useMutation(CREATE_USER);

        const assignCarersToCarehomes = async (userId, selectedCarehomeIds) => {
            
            for (const carehome of carehomes.value) {
                const currentlyHasUser = (carehome.users_permissions_users || []).some(u => u.documentId === userId);                
                
                const shouldHaveUser = selectedCarehomeIds.includes(carehome.documentId);

                if (shouldHaveUser && !currentlyHasUser) {
                    const updatedUsers = [
                        ...(carehome.users_permissions_users || []).map(u => u.documentId),
                        userId
                    ];
                    await updateCarehome({
                        id: carehome.documentId,
                        data: { users_permissions_users: updatedUsers }
                    });
                }

                if (!shouldHaveUser && currentlyHasUser) {
                    const updatedUsers = (carehome.users_permissions_users || [])
                        .filter(u => u.documentId !== userId)
                        .map(u => u.documentId);
                    await updateCarehome({
                        id: carehome.documentId,
                        data: { users_permissions_users: updatedUsers }
                    });
                }
            }
            await refetchCarehomes();
        };

        const carehomes = ref([]);
       
       const { result: carehomesResult, refetch: refetchCarehomes } = useQuery(GET_CAREHOMES_BY_USER,{userId:documentId});

        watchEffect(() => {
            if (carehomesResult.value?.carehomes) {
                carehomes.value = carehomesResult.value.carehomes;
            }
        });


        // Table fields
        const tableFields = [
            { key: 'fullname', label: 'Fullname' },
            { key: 'email', label: 'Email' },
            { key: 'role', label: 'Role', formatter: (value, key, item) => getRoleName(item.rawData.role?.id) },
            { key: 'confirmed', label: 'Confirmed' },
            { key: 'status', label: 'Status', formatter: (value, key, item) => item.rawData.blocked },
            { key: 'rateperhour', label: 'Rate/Hour' },
            { key: 'rateperday', label: 'Rate/Day' },
            { key: 'actions', label: 'Actions' }
        ];

        // Table items
        const tableItems = computed(() => {
            return users.value.map(user => ({
                ...user,
                rawData: user // Store the full user object for access in slots
            }));
        });
const employeeUsers = computed(() => {

    const filtered = users.value.filter(user => user.role?.id === 4); // 4 are employees
    
    return filtered;
});

const employeeTableItems = computed(() => {
    return employeeUsers.value.map(user => ({
        ...user,
        rawData: user
    }));
});	    
    
        // Form validation

        const fullnameState = computed(() => (userForm.value.fullname?.length || 0) > 0);
        const rateperhourState = computed(() => (userForm.value.rateperhour?.length || 0) > 0);
        const rateperdayState = computed(() => (userForm.value.rateperday?.length || 0) > 0);
        const emailState = computed(() => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userForm.value.email));

const passwordState = computed(() => {
  const password = userForm.value.password || "";

  // Edit mode + blank password = neutral, not invalid
  if (editingUser.value && password.length === 0) {
    return null;
  }

  // Create mode + blank password = invalid
  if (!editingUser.value && password.length === 0) {
    return false;
  }

  return passwordScore.value === 5;
});

     const roleState = computed(() => userForm.value.role !== null);

const formValid = computed(() => {
    const passwordValid =
        editingUser.value && !userForm.value.password
            ? true
            : passwordState.value;

    return (
        fullnameState.value &&
        emailState.value &&
        passwordValid &&
        roleState.value
    );
});

const {
  result: usersResult, refetch: usersRefetch
} = useQuery(GET_USERS_BY_ADMIN, {
    sort: ["fullname:asc"],
    admin: String(adminid).trim()
});


watchEffect(() => {
  if (usersResult.value?.usersPermissionsUsers) {
    //users.value = usersResult.value.usersPermissionsUsers;
  //console.log(users.value);
  }
});

watchEffect(() => {
  if (usersResult.value?.usersPermissionsUsers && roles.value.length > 0) {
    users.value = usersResult.value.usersPermissionsUsers.map(user => {
      const matchingRole = roles.value.find(role => role.documentId === user.role?.documentId);

      return {
        ...user,
        role: {
          ...user.role,  // Keep original GraphQL role data
          id: matchingRole?.id  // Add numeric id to the role object
        }
      };
    });
  }
});


        // Fetch all roles
        const fetchRoles = async () => {
            const allowedTypes = ['employees', 'viewers'];
            try {
                const response = await axios.get(`${API_URL}/api/users-permissions/roles`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                roles.value = response.data.roles.filter(role => allowedTypes.includes(role.name));
            } catch (error) {
                showError('Failed to fetch roles', error);
            }
        };

        const closeModal = async () => {
            //console.log('close');
            showModal.value = false;

        };

        // Add or update user
        const submitUser = async () => {
            if (!formValid.value) return;

            try {
                formLoading.value = true;
                const userData = {
                    fullname: userForm.value.fullname,
                    email: userForm.value.email,
                    username: userForm.value.email,
                    confirmed: userForm.value.confirmed,
                    role: userForm.value.role,
                    rateperhour: Number(userForm.value.rateperhour || 0),
                    rateperday: Number(userForm.value.rateperday || 0),
                    admin: String(adminid),
                    blocked: userForm.value.blocked
                };

                if (userForm.value.password) {
                    userData.password = userForm.value.password.trim();
                }

                let newUserDocumentId = null;
                if (editingUser.value) {
                   const result = await updateUser({ documentId: userForm.value.docId,  data: userData });
			if (result){ 
				//console.log(result);
			}

                    // Assign carer to selected carehome

                    if (userForm.value.carehomes && userForm.value.docId) {
                        await assignCarersToCarehomes(userForm.value.docId, userForm.value.carehomes);
                    }
                    //await refetchCarehomes();
                    await usersRefetch();
                    toast.success('Employee updated successfully');


                } else {
			const result = await createUser({
                         data: userData
                        });
			newUserDocumentId = result.data.createUsersPermissionsUser.data.documentId;

                    await usersRefetch();
                    toast.success('Employee created successfully');

                }
                // Assign carer to selected carehome

                if (userForm.value.carehomes && newUserDocumentId) {
                    await assignCarersToCarehomes(newUserDocumentId, userForm.value.carehomes);
                }
                showModal.value = false;
            } catch (error) {
                showError('Failed to save employee', error);
            } finally {
                formLoading.value = false;
            }
        };
        const findUserCarehomeIds = (userId) => {

            return carehomes.value
                .filter(ch =>
                    (ch.users_permissions_users || []).some(u => u.documentId === userId)
                )
                .map(ch => ch.documentId);
        };

        watch(() => userForm.value.carehomes, (newVal) => {
           //console.log('Selected office locations:', newVal);
		void newVal;
        });


        // Edit user
        const editUser = (user) => {
            userForm.value = {
                docId: user.documentId,
                id: user.id,
                fullname: user.fullname,
                rateperhour: user.rateperhour || 0.0,
                rateperday: user.rateperday || 0.0,
                email: user.email,
                password: '',
                confirmed: user.confirmed,
                role: user.role?.id || null,
                blocked: user.blocked || false,
                carehomes: findUserCarehomeIds(user.documentId)
            };
            //console.log(userForm.value);
            editingUser.value = true;
            showModal.value = true;
        };

        // Toggle user block status
        const toggleBlockUser = async (user) => {
            const action = user.blocked ? 'unblock' : 'block';
            if (!confirm(`Are you sure you want to ${action} ${user.fullname}?`)) return;

            try {
                   user.blocked = !user.blocked;
const result = await updateUser({
  documentId: user.documentId,
  data: {
    blocked: user.blocked 
  }
});
  if (result){
//console.log(result);
}


                toast.success(`Employee ${action}ed successfully`);
            } catch (error) {
                showError(`Failed to ${action} employee`, error);
            }
        };

        // Open modal for new user
        const openModal = () => {
            resetForm();
            showModal.value = true;
        };

        // Reset form
        const resetForm = () => {
            userForm.value = {
                id: null,
                fullname: '',
                email: '',
                password: '',
                rateperhour: 0.0,
                rateperday: 0.0,
                confirmed: true,
                role: null,
                blocked: false,
                carehomes: []
            };
            editingUser.value = false;
        };

        // Get role name by ID
        const getRoleName = (roleId) => {
            if (!roleId) return 'None';
            const foundRole = roles.value.find(r => r.id === roleId);
            return foundRole ? foundRole.name : ''; //or admin
        };

        // Show error message
const showError = (message, error) => {
   // console.error(message, error);

    const backendMessage =
        error.graphQLErrors?.[0]?.extensions?.error?.message ||
        error.graphQLErrors?.[0]?.message ||
        error.networkError?.result?.errors?.[0]?.extensions?.error?.message ||
        error.networkError?.result?.errors?.[0]?.message ||
        error.message;

    let userMessage = 'Something went wrong. Please try again.';

    if (backendMessage === 'Email already taken') {
        userMessage = 'This email address is already registered.';
    } else if (backendMessage?.includes('Float cannot represent')) {
        userMessage = 'Please enter a valid number for rates.';
    } else if (backendMessage) {
        userMessage = backendMessage;
    }

    toast.error(userMessage);
};

        // Initialize component
        onMounted(() => {
            fetchRoles();
        });

        return {
            users,
            roles,
            userForm,
            editingUser,
            showModal,
            loading,
            formLoading,
            tableFields,
            tableItems,
            employeeUsers,
            employeeTableItems,
            fullnameState,
            rateperhourState,
            rateperdayState,
            adminname,
            adminid,
            emailState,
            passwordState,
            roleState,
            formValid,
            toastMessage,
            getBread,
            openModal,
            submitUser,
            closeModal,
            editUser,
            toggleBlockUser,
            resetForm,
            getRoleName,
            carehomes,
            showPassword,
passwordStrength,
strengthClass,
strengthWidth,
        };
    }
};
</script>

<style scoped>
.container-fluid {
    max-width: 80%;
}

.table-responsive {
    overflow-x: auto;
}

.card {
    transition: all 0.3s ease;
}

.card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15);
}

@media (max-width: 767.98px) {
    .btn-sm {
        padding: 0.25rem 0.5rem;
        font-size: 0.75rem;
    }
}
.password-wrapper {
    position: relative;
}

.password-toggle {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
    border: none;
    background: transparent;
    cursor: pointer;
    font-size: 18px;
}

.password-strength {
    margin-top: 10px;
}

.strength-bar {
    height: 8px;
    background: #e5e7eb;
    border-radius: 999px;
    overflow: hidden;
}

.strength-fill {
    height: 100%;
    transition: 0.3s;
}

.strength-fill.weak {
    background: #ef4444;
}

.strength-fill.medium {
    background: #f59e0b;
}

.strength-fill.strong {
    background: #22c55e;
}

small.weak {
    color: #ef4444;
}

small.medium {
    color: #f59e0b;
}

small.strong {
    color: #22c55e;
}

.requirements {
    margin-top: 10px;
    padding-left: 18px;
    font-size: 13px;
    color: #888;
}

.requirements li {
    margin-bottom: 4px;
}

.requirements li.valid {
    color: #22c55e;
    font-weight: 600;
}					
</style>
